import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@design-system';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HorizontalScrollContainer } from '@/components/HorizontalScrollContainer';

import { Download, Loader2 } from 'lucide-react';

import {
  usePoliciesComparison,
  useColumnReorder,
  useDownloadComparisonPdf,
  useDragMonitor,
  useRowVisibilityStore,
  getCarrierImage,
  getPremiumBreakdowns,
  rankIrrAcrossPolicies,
  type PremiumBreakdown,
  DraggableColumnHeader,
  ColumnDragLayer,
  RowVisibilityToggle,
  Td,
  Tf,
  ThSticky,
  type NormalizedPolicy,
} from '@/features/policies-comparison';

type PoliciesComparisonModalProps =
  | { children: React.ReactNode; open?: never; onOpenChange?: never }
  | { children?: never; open: boolean; onOpenChange: (open: boolean) => void };

export function PoliciesComparisonModal({
  children,
  open,
  onOpenChange,
}: PoliciesComparisonModalProps) {
  const { clientId, clientName, advisorName, organizationName, data, flags, inflatedBenefitLabel } =
    usePoliciesComparison();

  const emptyContent = (
    <DialogContent className="w-full sm:max-w-[calc(100%-2rem)]">
      <DialogHeader className="px-8 text-start">
        <DialogTitle className="text-primary-dark text-2xl">Policy Comparison</DialogTitle>
      </DialogHeader>
      <div className="px-8 py-6">
        <p className="text-foreground">
          No valid policies found to compare. Please ensure policies have the required data.
        </p>
      </div>
    </DialogContent>
  );

  if (!data || data.length === 0) {
    if (children) {
      return (
        <Dialog>
          <DialogTrigger asChild>{children}</DialogTrigger>
          {emptyContent}
        </Dialog>
      );
    }
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        {emptyContent}
      </Dialog>
    );
  }

  return (
    <PoliciesComparisonModalContent
      clientId={clientId}
      clientName={clientName}
      advisorName={advisorName}
      organizationName={organizationName}
      data={data}
      flags={flags}
      inflatedBenefitLabel={inflatedBenefitLabel}
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
    </PoliciesComparisonModalContent>
  );
}

interface PoliciesComparisonModalContentProps {
  children?: React.ReactNode;
  clientId: string;
  clientName: string;
  advisorName: string;
  organizationName?: string;
  data: NormalizedPolicy[];
  flags: { nycLifeDemo?: boolean };
  inflatedBenefitLabel: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function PoliciesComparisonModalContent({
  children,
  clientId,
  clientName,
  advisorName,
  organizationName,
  data,
  flags,
  inflatedBenefitLabel,
  open,
  onOpenChange,
}: PoliciesComparisonModalContentProps) {
  const { visibleRows } = useRowVisibilityStore();
  const orderedDataRef = useRef<NormalizedPolicy[]>(data);

  const { downloadPdf, isPending: isDownloading } = useDownloadComparisonPdf({
    orderedDataRef,
    inflatedBenefitLabel,
    nycLifeDemo: flags.nycLifeDemo,
    clientName,
    advisorName,
    organizationName,
  });

  const handleOrderChange = useCallback((ordered: NormalizedPolicy[]) => {
    orderedDataRef.current = ordered;
  }, []);

  const dialogContent = (
    <DialogContent className="grid max-h-[95dvh] w-full grid-rows-[auto,1fr] gap-3 overflow-scroll rounded-2xl px-0 pb-0 pt-3 sm:max-w-[calc(100%-2rem)] md:gap-4 md:rounded-3xl md:pt-6">
      <DialogHeader className="flex flex-row items-center gap-4 px-4 text-start md:px-8">
        <DialogTitle className="text-primary-dark text-lg md:text-2xl">
          Policy Comparison
        </DialogTitle>
        <div className="flex items-center gap-2">
          <RowVisibilityToggle />
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadPdf(visibleRows)}
            disabled={isDownloading}
          >
            {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
            <span className="hidden sm:inline">
              {isDownloading ? 'Generating...' : 'Download PDF'}
            </span>
          </Button>
        </div>
      </DialogHeader>
      <div className="relative flex flex-col overflow-hidden pb-3 pl-0 md:mt-2 md:rounded-l-2xl md:pb-4 md:pl-2">
        <div className="bg-background absolute left-0 top-0 z-10 hidden h-[calc(100%-52px)] w-16 sm:block" />
        <div className="md:w-85 md:max-w-85 bg-background absolute left-8 top-0 z-10 hidden h-[calc(100%-52px)] w-64 max-w-64 rounded-2xl shadow-2xl shadow-black/10 md:block" />
        <div className="relative flex overflow-y-auto">
          <HorizontalScrollContainer scrollbarClassName="mt-5 mx-6 w-[calc(100%-48px)] overflow-hidden rounded-full">
            <DndProvider backend={HTML5Backend}>
              <ComparisonTable
                clientId={clientId}
                data={data}
                flags={flags}
                inflatedBenefitLabel={inflatedBenefitLabel}
                onOrderChange={handleOrderChange}
              />
            </DndProvider>
          </HorizontalScrollContainer>
        </div>
      </div>
    </DialogContent>
  );

  if (children) {
    return (
      <Dialog>
        <DialogTrigger asChild>{children}</DialogTrigger>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      {dialogContent}
    </Dialog>
  );
}

interface ComparisonTableProps {
  clientId: string;
  data: NormalizedPolicy[];
  flags: { nycLifeDemo?: boolean };
  inflatedBenefitLabel: string;
  onOrderChange: (orderedData: NormalizedPolicy[]) => void;
}

function ComparisonTable({
  clientId,
  data,
  flags,
  inflatedBenefitLabel,
  onOrderChange,
}: ComparisonTableProps) {
  const { orderedData, hoverColumn, commitDrop, cancelDrag } = useColumnReorder(data, clientId);
  const { draggedId } = useDragMonitor();
  const { visibleRows, toggleRow } = useRowVisibilityStore();

  const rankedBreakdowns = useMemo(() => {
    const allBreakdowns = orderedData.map(p => getPremiumBreakdowns(p));
    rankIrrAcrossPolicies(allBreakdowns);
    return allBreakdowns;
  }, [orderedData]);

  const onOrderChangeRef = useRef(onOrderChange);
  onOrderChangeRef.current = onOrderChange;

  useEffect(() => {
    onOrderChangeRef.current(orderedData);
  }, [orderedData]);

  function getDragCellClassName(policyId: string) {
    if (!draggedId) return undefined;
    if (draggedId === policyId) return 'opacity-40';
    return 'transition-transform duration-200';
  }

  return (
    <>
      <ColumnDragLayer />
      <table className="w-max table-fixed border-separate border-spacing-x-3 md:border-spacing-x-6">
        <thead>
          <tr>
            <ThSticky>Solution Provider</ThSticky>
            {orderedData.map((policy, index) => {
              const carrierImage = getCarrierImage(policy.carrierId);
              return (
                <DraggableColumnHeader
                  key={policy.fundingSourceId}
                  fundingSourceId={policy.fundingSourceId}
                  index={index}
                  title={flags.nycLifeDemo ? 'Insurance' : (policy.productName ?? 'Insurance')}
                  img={flags.nycLifeDemo ? `/logos/nyl-logo.png` : carrierImage?.src}
                  imgClassName={flags.nycLifeDemo ? 'w-12 md:w-12' : carrierImage?.size}
                  draggedId={draggedId}
                  onHover={hoverColumn}
                  onDrop={commitDrop}
                  onDragCancel={cancelDrag}
                />
              );
            })}
          </tr>
        </thead>
        <tbody className="[&>tr:last-child>td]:border-b-0">
          {visibleRows.monthlyBenefit && (
            <tr className="align-top">
              <Td
                isSticky
                title="Monthly Benefit"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('monthlyBenefit')}
              >
                <ul className="text-primary-dark mt-2 space-y-2 pl-1 text-xs md:mt-4 md:text-sm">
                  <li>Day One</li>
                  <li>{inflatedBenefitLabel}</li>
                </ul>
              </Td>
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  header="Monthly Benefit"
                  className={getDragCellClassName(policy.fundingSourceId)}
                >
                  <ul className="text-primary-dark space-y-2 text-sm font-medium md:text-base">
                    <li>
                      <span className="text-foreground inline-block text-xs sm:hidden">
                        Day One:&nbsp;
                      </span>
                      <span>{policy.initialMonthlyBenefit}</span>
                    </li>
                    <li>
                      <span className="text-foreground inline-block text-xs sm:hidden">
                        {inflatedBenefitLabel}:&nbsp;
                      </span>
                      <span>{policy.inflatedMonthlyBenefit}</span>
                    </li>
                  </ul>
                </Td>
              ))}
            </tr>
          )}
          {visibleRows.totalBenefit && (
            <tr className="align-top">
              <Td
                isSticky
                title="Total Benefit"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('totalBenefit')}
              >
                <ul className="text-primary-dark mt-2 space-y-2 pl-1 text-xs md:mt-4 md:text-sm">
                  <li>Day One</li>
                  <li>{inflatedBenefitLabel}</li>
                </ul>
              </Td>
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  header="Total Benefit"
                  className={getDragCellClassName(policy.fundingSourceId)}
                >
                  <ul className="text-primary-dark space-y-2 text-sm font-medium md:text-base">
                    <li>
                      <span className="text-foreground inline-block text-xs sm:hidden">
                        Day One:&nbsp;
                      </span>
                      <span>{policy.initialTotalBenefit}</span>
                    </li>
                    <li>
                      <span className="text-foreground inline-block text-xs sm:hidden">
                        {inflatedBenefitLabel}:&nbsp;
                      </span>
                      <span>{policy.inflatedTotalBenefit}</span>
                    </li>
                  </ul>
                </Td>
              ))}
            </tr>
          )}
          {visibleRows.growth && (
            <tr className="align-top">
              <Td
                isSticky
                title="Growth"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('growth')}
              />
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  title={policy.inflationDescription ?? '-'}
                  header="Growth"
                  className={getDragCellClassName(policy.fundingSourceId)}
                />
              ))}
            </tr>
          )}
          {visibleRows.eliminationPeriod && (
            <tr className="align-top">
              <Td
                isSticky
                title="Elimination Period"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('eliminationPeriod')}
              />
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  title={policy.eliminationPeriodLabel ?? '-'}
                  header="Elimination Period"
                  className={getDragCellClassName(policy.fundingSourceId)}
                />
              ))}
            </tr>
          )}
          {visibleRows.policyType && (
            <tr className="align-top">
              <Td
                isSticky
                title="Policy Type"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('policyType')}
              />
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  title={policy.productDescription ?? '-'}
                  header="Policy Type"
                  className={getDragCellClassName(policy.fundingSourceId)}
                />
              ))}
            </tr>
          )}
          {visibleRows.methodBenefitsPaid && (
            <tr className="align-top">
              <Td
                isSticky
                title="Method Benefits Paid"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('methodBenefitsPaid')}
              />
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  title={policy.paymentMethod ?? '-'}
                  header="Method Benefits Paid"
                  className={getDragCellClassName(policy.fundingSourceId)}
                />
              ))}
            </tr>
          )}
          {visibleRows.irr && (
            <tr className="align-top">
              <Td
                isSticky
                title="IRR for Modeled LTC Event"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('irr')}
              />
              {orderedData.map((policy, colIndex) => (
                <td
                  key={policy.fundingSourceId}
                  className={cn(
                    'border-primary/15 bg-background border-x border-b px-3 py-3 align-middle md:px-6 md:py-4',
                    getDragCellClassName(policy.fundingSourceId),
                  )}
                >
                  <div className="text-center">
                    <IrrCell breakdowns={rankedBreakdowns[colIndex]} />
                  </div>
                </td>
              ))}
            </tr>
          )}
          {visibleRows.premiumGuarantee && (
            <tr className="align-top">
              <Td
                isSticky
                title="Are premium amounts guaranteed?"
                className="hidden sm:table-cell"
                onHide={() => toggleRow('premiumGuarantee')}
              />
              {orderedData.map(policy => (
                <Td
                  key={policy.fundingSourceId}
                  title={policy.premiumGuaranteed ?? '-'}
                  header="Are premium amounts guaranteed?"
                  className={getDragCellClassName(policy.fundingSourceId)}
                />
              ))}
            </tr>
          )}
        </tbody>
        <PremiumFooter
          orderedData={orderedData}
          rankedBreakdowns={rankedBreakdowns}
          getDragCellClassName={getDragCellClassName}
        />
      </table>
    </>
  );
}

interface PremiumFooterProps {
  orderedData: NormalizedPolicy[];
  rankedBreakdowns: PremiumBreakdown[][];
  getDragCellClassName: (policyId: string) => string | undefined;
}

function PremiumFooter({
  orderedData,
  rankedBreakdowns,
  getDragCellClassName,
}: PremiumFooterProps) {
  return (
    <tfoot>
      <tr>
        <td className="border-primary/15 text-primary-dark md:w-85 md:max-w-85 bg-background relative z-20 hidden w-64 max-w-64 rounded-b-xl border p-3 text-left sm:sticky sm:left-3 sm:table-cell md:left-6 md:rounded-b-2xl md:p-6">
          <div className="text-sm font-medium md:text-base">Premium</div>
        </td>
        {orderedData.map((policy, colIndex) => {
          const breakdowns = rankedBreakdowns[colIndex];
          const hasAny = breakdowns.some(b => b.hasMonthlyCost || b.hasLumpSum);

          return (
            <Tf
              key={policy.fundingSourceId}
              className={getDragCellClassName(policy.fundingSourceId)}
            >
              {hasAny ? (
                breakdowns.map((b, i) => (
                  <div key={i}>
                    {i > 0 && (
                      <div className="text-primary-dark/50 my-2 flex items-center gap-2 text-xs italic">
                        <div className="bg-primary/30 h-px flex-1" />
                        <span>or</span>
                        <div className="bg-primary/30 h-px flex-1" />
                      </div>
                    )}
                    <div className="text-sm md:text-base">
                      {b.hasMonthlyCost && b.hasLimitedPay ? (
                        <>
                          <strong>{b.annualPremiumFormatted}</strong> - Annual Premium for{' '}
                          {b.limitedPayYears} years. Then no further premiums required.
                        </>
                      ) : b.hasMonthlyCost ? (
                        <>
                          <strong>{b.monthlyPremiumFormatted}</strong>/month
                        </>
                      ) : b.hasLumpSum ? (
                        <>
                          <strong>{b.lumpSumFormatted}</strong> - Single Premium
                        </>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm md:text-base">-</div>
              )}
            </Tf>
          );
        })}
      </tr>
    </tfoot>
  );
}

function IrrCell({ breakdowns }: { breakdowns: PremiumBreakdown[] }) {
  const withIrr = breakdowns.filter(b => b.irrFormatted);
  if (withIrr.length === 0) return <span className="text-sm md:text-base">-</span>;

  return (
    <ul className="text-primary-dark space-y-2 text-sm font-medium md:text-base">
      {withIrr.map((b, i) => (
        <li
          key={i}
          className="flex items-center justify-center gap-1.5"
        >
          <span>{b.irrFormatted}</span>
          {b.irrRank === 1 && <IrrBadge rank={1} />}
          {b.irrRank === 2 && <IrrBadge rank={2} />}
        </li>
      ))}
    </ul>
  );
}

function IrrBadge({ rank }: { rank: 1 | 2 }) {
  if (rank === 1) {
    return <Badge variant="success">Highest IRR</Badge>;
  }

  return <Badge variant="warning">Second Highest IRR</Badge>;
}
